// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * VR controller interaction layer.
 *
 * Wires the two XR controllers' input events (trigger = `select`,
 * grip = `squeeze`, thumbstick via gamepad API) into the semantic
 * actions the scene cares about:
 *
 *   - Trigger-drag while pointing at the globe  → rotate the globe
 *   - Trigger-click on a HUD button             → fire the HUD action
 *   - Grip on the globe                         → grab-and-carry (reposition)
 *   - Left thumbstick Y                         → the globe's size
 *   - Right thumbstick Y, aimed at the globe    → zoom in at that place
 *     (AYNI, owner 2026-10-22: vrFocusZoom.ts; trigger-drag then moves
 *     the map under the magnified spot)
 *
 * The module is intentionally decoupled from session management:
 * `vrSession.ts` calls `update()` every frame and hands it the
 * session's callbacks. All scene/mesh references come
 * in through the context so this can be re-used for the multi-globe
 * Phase 2.5 work without changing its internals.
 *
 * See {@link file://./../../docs/VR_INVESTIGATION_PLAN.md VR_INVESTIGATION_PLAN.md}.
 */

import type * as THREE from 'three'
import type { XRControllerModelFactory } from 'three/examples/jsm/webxr/XRControllerModelFactory.js'
import type { VrHudAction, VrHudHandle } from './vrHud'
import { panelActionsEqual, type VrPanelAction, type VrSidePanelHandle } from './vrSidePanel'
import type { VrPlacementHandle } from './vrPlacement'
import type { VrKhipuAction, VrKhipuPanelHandle } from './vrKhipuPanel'
import type { VrLegendAction, VrLegendHandle } from './vrLegend'
import type { VrTimelineTrackHandle } from './vrTimelineTrack'
import type { VrWorldPanelsHandle } from './vrWorldPanels'
import { SCRUB_SEEK_INTERVAL_MS } from './timelineTrackCanvas'
import { MAX_GLOBE_SCALE, MIN_GLOBE_SCALE } from './vrScene'
import {
  MAX_FOCUS_MAGNIFICATION,
  commitFocusZoom,
  displayToSource,
  displayUvToSourceUv,
  focusZoomVersion,
  getFocusZoom,
  isZoomed,
  panTo,
  uvToDisplayDir,
  zoomBy,
  type Quat,
  type Vec3,
} from './vrFocusZoom'
import { logger } from '../utils/logger'
import { emit } from '../analytics'
import { t } from '../i18n'
import type { VrGesture } from '../types'

/** Per-gesture rate cap. Tier B `vr_interaction` events fire once
 * per discrete gesture (drag release, pinch release, flick start,
 * thumbstick-zoom release, hud tap), but a user with twitchy
 * controllers can produce many in quick succession. Cap each
 * gesture type independently so a flurry of one type doesn't
 * starve the others off the wire. */
export const VR_INTERACTION_MAX_PER_MINUTE = 30
const VR_INTERACTION_WINDOW_MS = 60_000
const vrInteractionTimes = new Map<VrGesture, number[]>()

/** Emit a `vr_interaction` event if the per-gesture throttle
 * permits. Magnitude semantics vary per gesture (see emit sites)
 * but always rounded to 2 decimals so the wire stays narrow.
 *
 * Exported for tests; production callers should reach for it via
 * the gesture-specific emit sites in this file. */
export function emitVrInteraction(gesture: VrGesture, magnitude: number): void {
  const now = Date.now()
  let times = vrInteractionTimes.get(gesture)
  if (!times) {
    times = []
    vrInteractionTimes.set(gesture, times)
  }
  // Drop expired entries from the front of the window.
  const cutoff = now - VR_INTERACTION_WINDOW_MS
  while (times.length > 0 && times[0] < cutoff) times.shift()
  if (times.length >= VR_INTERACTION_MAX_PER_MINUTE) return
  times.push(now)
  emit({
    event_type: 'vr_interaction',
    gesture,
    magnitude: Math.round(magnitude * 100) / 100,
  })
}

/** Test-only — clears the throttle state so consecutive tests
 * don't leak emit counts between cases. */
export function __resetVrInteractionThrottleForTests(): void {
  vrInteractionTimes.clear()
}

/**
 * Thumbstick reading below this magnitude is treated as zero. Quest
 * thumbsticks rest slightly off-center; without a deadzone the globe
 * creeps when the user isn't touching anything.
 */
const THUMBSTICK_DEADZONE = 0.15

/** AYNI (Quest review B9): where the controller models are served, on our own host. */
export const XR_INPUT_PROFILES_PATH = '/xr-input-profiles'

/**
 * Zoom rate at full-deflection — scale factor multiplier per second.
 *
 * At 2.5 it takes ~1 s at full thumbstick to cross half the
 * MIN→MAX range, which testers reported feels responsive without
 * being twitchy. The previous value of 1.3 took ~3.5 s for the same
 * range — called "slightly slow" on-headset.
 */
const ZOOM_RATE_PER_SECOND = 2.5

/**
 * AYNI (owner, 2026-10-22): the right stick's zoom (vrFocusZoom.ts), as a
 * magnification factor per second at full deflection: 8× a second goes from
 * the whole Earth to Barcelona's streets (8192×) in about 4.5 s.
 */
const FOCUS_ZOOM_RATE_PER_SECOND = 8

/**
 * Structural slice of `XRInputSource` this module reads for the
 * thumbstick. Deliberately minimal so the rule below is unit-testable
 * with no XR session — the same session-like seam `vrCapability` takes.
 */
export interface XRInputSourceLike {
  readonly targetRayMode?: string
  readonly handedness?: string
  readonly gamepad?: { readonly axes: ArrayLike<number> } | null
}

/**
 * Thumbstick Y deflection for one stashed input source, or `null` when
 * that source cannot carry a stick at all.
 *
 * Read this instead of `source.gamepad` directly. Only a
 * `tracked-pointer` source describes a held controller; a screen,
 * transient-pointer or gaze source either has no gamepad or — Chrome on
 * Android — a gamepad whose axes ARE the touch position. Taking the
 * latter for a stick is what made "any press" resize the globe: the
 * finger's Y arrived as `axes[1]`, and the zoom path applied it on
 * every contact, including during the placement height step.
 *
 * A capability test, deliberately not a device test. The coarser
 * `isScreenInput` gate above it stands a whole session down on a
 * classification that is right for today's phones; this one holds for
 * any source, on any device, whatever that classification said.
 */
export function thumbstickAxisY(
  source: XRInputSourceLike | null | undefined,
): number | null {
  if (!source || source.targetRayMode !== 'tracked-pointer') return null
  const gamepad = source.gamepad
  if (!gamepad) return null
  // Each Quest controller maps axes [2, 3] to the thumbstick (axes
  // [0, 1] are the touchpad, which the Quest doesn't have), hence the
  // fallback. A non-finite reading is rejected rather than clamped:
  // `Math.abs(NaN) <= THUMBSTICK_DEADZONE` is false, so a NaN axis used
  // to slip past the deadzone check rather than being caught by it.
  const y = gamepad.axes[3] ?? gamepad.axes[1] ?? 0
  return Number.isFinite(y) ? y : null
}

/**
 * AYNI (owner, 2026-10-22): what a controller's stick does. The right one
 * zooms in where it points (vrFocusZoom.ts); the left one, and a controller
 * that does not say which hand holds it, sets the globe's size.
 */
export function stickRole(source: XRInputSourceLike | null | undefined): 'size' | 'zoom' {
  return source?.handedness === 'right' ? 'zoom' : 'size'
}

/**
 * Smallest hand separation a two-hand gesture may start from, in metres.
 *
 * The pinch applies `currentDistance / startDistance` to the globe's
 * scale, so a start separation near zero turns every subsequent
 * millimetre into a huge scale change — the globe runs away on contact
 * rather than on a gesture. That is not hypothetical: a phone's taps
 * arrive as *transient* input sources whose world positions sit at the
 * device, so a pair of them are centimetres apart at best, and the
 * ratio is meaningless. Two hands deliberately posed for a pinch are
 * tens of centimetres apart, so a 5 cm floor costs nothing there and
 * takes the runaway away here. Inputs that cannot clear it fall back to
 * idle, and the release-and-regrab the old guard already documented
 * applies unchanged.
 */
const MIN_TWO_HAND_SEPARATION_M = 0.05

/**
 * Multiplier applied to every rotation delta (single-hand and
 * two-hand). On-headset feedback called the rotation "slow…
 * doesn't quite keep up with the controllers". The native rotation
 * rates are geometry-bound (surface-pinned) or input-bound
 * (rigid-body): both can produce sub-1:1 globe rotation per unit
 * of physical motion, which feels sluggish.
 *
 * 2.0 doubles the rotation produced per unit of input. Trade-off:
 * surface-pinning becomes approximate (the grab point drifts under
 * the ray during fast drags) but the rotation amount feels closer
 * to the user's expectation of "the globe matches my motion". If
 * 2.0 still feels slow, try 3.0; if it overshoots, drop to 1.5.
 */
const ROTATION_SENSITIVITY = 2.0

/**
 * Inertia parameters — match the 2D MapLibre "flick the globe and it
 * keeps spinning" feel.
 *
 * - `MIN_INERTIA_SPEED`: kick-off threshold (rad/s). Slow drags below
 *   this produce no inertia (no annoying micro-spins after a careful
 *   release). Also serves as the stop threshold — inertia ends when
 *   decayed velocity drops below it.
 * - `INERTIA_TIME_CONSTANT`: exponential decay τ in seconds. Velocity
 *   decays to 37 % at 1τ, 13 % at 2τ, 5 % at 3τ.
 * - `VELOCITY_SMOOTHING_ALPHA`: how much the per-frame measured
 *   velocity contributes to the running average. Lower = more
 *   smoothing, less jitter, but also more lag in capturing a flick.
 *
 * Tuning history:
 *   - τ=1.5 s, min=0.5 → spins lasted ~3.5 s. On-headset feedback:
 *     "stops should be a little snappier".
 *   - τ=0.7 s, min=1.0 → confident flicks last ~1 s, casual drags
 *     decay almost immediately. Current values.
 */
const MIN_INERTIA_SPEED = 1.0
const INERTIA_TIME_CONSTANT = 0.7
const VELOCITY_SMOOTHING_ALPHA = 0.4

/**
 * Length of the visible controller ray in metres. Matches the
 * Three.js WebXR examples' convention.
 */
const RAY_LENGTH = 5

export interface VrInteractionContext {
  scene: THREE.Scene
  /** Primary globe — scene slot 0. Drives rotation + surface-pinned raycast. */
  globe: THREE.Mesh
  /**
   * Every globe in the current layout (primary + secondaries). Used
   * for multi-globe hit-testing so grab/rotate picks up whichever
   * globe the controller is pointing at — the surface-pinned drag
   * math needs the hit globe's position as the pivot. For a 1-globe
   * session this is just `[globe]`. Function (not a snapshot) so
   * panel-count changes mid-session are reflected without
   * re-creating the interaction handle.
   */
  getAllGlobes: () => THREE.Mesh[]
  hud: VrHudHandle
  /** The side panel (Browse, Layers, Data, View, Help; vrSidePanel). Named for the browse panel it replaced. */
  browse: VrSidePanelHandle
  /** AYNI: the playing Khipu's panel (vrKhipuPanel); hit-tested only while shown. */
  khipu?: VrKhipuPanelHandle
  /** AYNI: a press on the Khipu panel's buttons. */
  onKhipuAction?: (action: VrKhipuAction) => void
  /** AYNI (2026-10-11): the legend beside the globe (vrLegend); hit-tested only while shown. */
  legend?: VrLegendHandle
  /** A press on its ✕. */
  onLegendAction?: (action: VrLegendAction) => void
  /**
   * In-VR date track for the primary dataset's time axis. Draws itself
   * only while a timeline is loaded (`isVisible()`), and is hit-tested
   * before the globe so a drag on the strip scrubs the series rather than
   * spinning it.
   */
  timeline: VrTimelineTrackHandle
  /** AR-only spatial placement. Null in VR mode or when hit-test isn't available. */
  placement: VrPlacementHandle | null
  renderer: THREE.WebGLRenderer
  /** Fired when the user taps a dock button, with where on the dock (the progress bar reads it). */
  onHudAction: (action: VrHudAction, uv: { x: number; y: number }) => void
  /** Fired when the user interacts with the browse panel (close / select dataset). */
  onBrowseAction: (action: VrPanelAction) => void
  /**
   * Fired when the user seeks on the date track: `preview` repeatedly
   * while a headset drag is in flight (rationed here — a DASH seek per XR
   * frame would be ninety seeks a second), then `commit` once on release.
   * A handheld only ever gets `commit`, because its touch is rotate-only
   * and a drag across the strip must not also move the series.
   */
  onTimelineSeek?: (progress: number, phase: 'preview' | 'commit') => void
  /** Fired when the user taps the floating Place button — caller toggles Place mode. */
  onPlaceButton: () => void
  /** Fired when the user pulls trigger while in Place mode — caller anchors the globe. */
  onPlaceConfirm: () => void
  /**
   * True for the whole session when it is running on a handheld (an
   * Android phone or tablet) rather than a headset. Every XR path that
   * can write the globe's scale — the two-hand pinch and the thumbstick
   * zoom — stands down when this is set: a phone's taps arrive as
   * transient sources whose positions sit at the device (so a pinch
   * ratio is meaningless), and Chrome hands the screen-tap source a
   * gamepad whose axes are the touch position (so the thumbstick path
   * reads a finger as a stick push). The DOM zoom slider is the only
   * sizing control there. A session-lifetime constant on the caller's
   * side, never recomputed from the input sources. Optional so
   * controller-only callers/tests can omit it (defaults to false).
   */
  isScreenInput?: () => boolean
  /**
   * True while the handheld rotate layer (`vrRotateTouch`) is mounted
   * and owns the globe's rotation. When set, this layer declines to
   * grab the globe on a select so one drag cannot rotate through both
   * paths. Narrower than `isScreenInput`: a handheld with no DOM
   * overlay has no rotate layer but is still a handheld, and must
   * still not pinch.
   */
  domTouchActive?: () => boolean
  /**
   * Fired when globe manipulation settles: trigger-drag release
   * (to idle, not inertia), two-hand pinch release, flick-inertia
   * decay stop, or thumbstick-zoom release. Caller computes the
   * gaze-to-globe lat/lon and emits `camera_settled` with
   * `projection='vr'|'ar'`. The per-session throttle in
   * `analytics/camera.ts` rate-limits the emit across 2D + VR.
   */
  onCameraSettled?: () => void
  /**
   * AYNI (Quest review E2, 2026-11-03): a grip has picked the globe up to
   * carry it. The session lets go of its placement, so the surface hold
   * and the anchor no longer pull the carried globe back.
   */
  onCarryStart?: () => void
  /**
   * AYNI (owner, 2026-11-04): false while the grip may not pick the globe
   * up: inside a globe grown past 2 m, which holds its centre at the eyes
   * (vrInsideView). Absent: it always may.
   */
  canCarry?: () => boolean
  /**
   * AYNI (owner on Quest, 2026-11-04): the small panels beside the globe
   * (vrWorldPanels); those with a ✕ take presses, before the globe.
   */
  worldPanels?: Pick<VrWorldPanelsHandle, 'pick' | 'pressable'>
  /** A press released on a small panel's ✕ (the satellite card's lets the satellite go). */
  onWorldPanelClose?: (id: string) => void
  /**
   * AYNI: fired when a trigger press (or a phone's tap) on a globe is
   * released within half a second without turning it — a tap on the
   * place under the ray, with the mesh-local UV THREE reports for it
   * (`sphereUvToLatLon` turns it into a place). A grab that rotated the
   * globe never fires it.
   */
  onGlobeTap?: (uv: THREE.Vector2, mesh: THREE.Mesh) => void
  /**
   * AYNI: A or X pressed while a controller aims at a globe — drop a pin
   * there (owner, 2026-10-04: the trigger turns the globe, so pins need a
   * button of their own). Controllers only; a phone drops pins by a tap.
   */
  onGlobePin?: (uv: THREE.Vector2) => void
  /**
   * AYNI (2026-10-22): how far the right stick may zoom in now, as a
   * magnification (vrFocusZoom.focusZoomCeiling for the place in focus and
   * the dataset shown). Absent: the deepest anywhere.
   */
  focusZoomCeiling?: () => number
  /**
   * AYNI (satellites plan): a press released soon without turning the
   * globe, wherever it aimed (on the globe or in the room), offered as a
   * ray before it counts as a tap on the globe. Return true to take it
   * (a satellite was picked): the globe tap is then dropped.
   */
  onRayTap?: (origin: THREE.Vector3, direction: THREE.Vector3) => boolean
  /**
   * AYNI (owner, 2026-10-24, Fully VR): true while the sky and the globe are
   * one system. A trigger pressed on nothing then grabs the sky: the point
   * under the ray follows it, and the globe turns by the same rotation (the
   * sky follows the globe's turn, so this is the same as turning the globe).
   * Controllers only; a phone's one-finger drag already turns the globe.
   */
  canGrabSky?: () => boolean
}

/**
 * AYNI (Fully VR): the globe's orientation after the sky was dragged from
 * `startDir` to `currentDir` (unit rays from the viewer): the same turn,
 * applied to the globe as it was when the drag began.
 */
export function skyDragGlobeQuaternion(
  THREE_: typeof THREE,
  globeStart: THREE.Quaternion,
  startDir: THREE.Vector3,
  currentDir: THREE.Vector3,
  out: THREE.Quaternion,
): THREE.Quaternion {
  const turn = new THREE_.Quaternion().setFromUnitVectors(startDir, currentDir)
  return out.copy(globeStart).premultiply(turn)
}

/** A press on the globe held longer than this is a grab, not a tap. */
const GLOBE_TAP_MAX_MS = 500
/** A press that turned the globe more than this (about 1°) is a rotation, not a tap. */
const GLOBE_TAP_MAX_TURN_RAD = 0.02
/** AYNI: how long the controller hints stay, and how many globe grabs make them redundant. */
const TOOLTIP_SHOW_MS = 25_000
const TOOLTIP_GRABS = 3
/** AYNI: A / X on an xr-standard gamepad (Quest Touch, PICO 4 and Neo 3 alike): the pin button. */
export const PIN_BUTTON_INDEX = 4

/**
 * AYNI (Pico + Wolvic, 2026-11-03): dispatch one `inputsourceschange` on
 * the session for the input sources it reports that `seen` (the sources the
 * controllers' `connected` listeners received) does not hold, so three's
 * handler ties them to controllers created after the session started (see
 * the call in `createVrInteraction`). Nothing happens when every source was
 * seen. Returns how many were announced. three reads only `added` and
 * `removed`; the app's own listener (`updateInputClass`) re-reads the
 * session's sources, so the extra event changes nothing else.
 */
export function replayUnseenInputSources(
  session: Pick<XRSession, 'inputSources' | 'dispatchEvent'> | null | undefined,
  seen: ReadonlyArray<XRInputSource | null>,
): number {
  if (!session) return 0
  const unseen = Array.from(session.inputSources as Iterable<XRInputSource>).filter((s) => !!s && !seen.includes(s))
  if (unseen.length === 0) return 0
  const event = new Event('inputsourceschange')
  Object.defineProperties(event, {
    session: { value: session },
    added: { value: unseen },
    removed: { value: [] },
  })
  session.dispatchEvent(event)
  return unseen.length
}

export interface VrInteractionHandle {
  /** Drive per-frame polling (thumbstick zoom, drag tracking). */
  update(deltaSeconds: number): void
  /** AYNI (2026-10-22): a trigger holds the globe (one or both), so an auto-rotate waits. */
  isGrabbing(): boolean
  /** AYNI (owner, 2026-11-04): a grip carries the globe, which owns its position meanwhile. */
  isCarrying(): boolean
  /**
   * Mesh-local UV under whichever controller is currently aimed at a
   * globe, or null when neither is. Drives the data-encoded value
   * readout — the in-VR equivalent of the 2D globe's pointer position.
   *
   * Raycasts on demand rather than caching per frame, because the only
   * caller is the readout and it is already throttled by the HUD's own
   * update cadence.
   */
  globeHoverUv(): { x: number; y: number } | null
  /**
   * AYNI: the same, with the world point the ray meets the globe at, for
   * the readout drawn beside it (vrReadout).
   */
  globeHover(): { uv: { x: number; y: number }; point: THREE.Vector3 } | null
  dispose(): void
}

/**
 * Rotation mode — mutually-exclusive union of the three states the
 * globe can be in based on how many triggers are on the globe:
 *
 * - `idle` — no rotation happening
 * - `single` — one trigger on globe; surface-pinned raycast drag.
 *   Lateral controller motion rotates the globe because the grab
 *   point stays pinned under the current ray. Matches MapLibre's
 *   "drag the surface under your cursor" feel.
 * - `two-hand` — both triggers on globe; mobile-style pinch +
 *   rigid-body rotate. Distance between controllers scales the
 *   globe; orientation is built from the axis between the hands
 *   plus the average of their up-vectors, giving a full 3-DoF
 *   rotation that includes wrist-roll / twist around the
 *   connecting axis. See `computeTwoHandOrientation` for the math.
 *
 * Transitions recapture the baseline so switching modes never
 * introduces drift. Thumbstick zoom is active in `idle` + `single`
 * but suppressed in `two-hand` because pinch already handles zoom
 * and the two inputs would fight.
 */
type RotationMode =
  | { kind: 'idle' }
  | {
      kind: 'single'
      index: 0 | 1
      /** The specific globe mesh the user grabbed — may be any globe in the arc. */
      grabbedGlobe: THREE.Mesh
      /** Unit vector from globe center to grabbed surface point, world-space. */
      worldGrabDir: THREE.Vector3
      /** Globe quaternion when the single drag began. */
      globeStartQuat: THREE.Quaternion
      /**
       * AYNI (2026-10-22): while the headset's zoom is on, the place
       * grabbed (globe frame); the drag then moves the map under the
       * magnified spot instead of turning the globe (vrFocusZoom.ts).
       */
      panSource: Vec3 | null
    }
  | {
      kind: 'two-hand'
      /**
       * Orientation of the virtual "rigid body" defined by the two
       * controllers at gesture start. Derived from the axis between
       * them (primary direction) + the average of each controller's
       * up-vector projected onto the perpendicular plane. Capturing
       * this — instead of just the axis direction — means wrist
       * twists around the connecting axis also rotate the globe,
       * which matches the user's mental model of "grabbing the
       * globe with both hands".
       */
      startOrientation: THREE.Quaternion
      /** Distance between controllers when the two-hand gesture began. */
      startDistance: number
      /** Globe uniform scale when the two-hand gesture began. */
      startScale: number
      /** Globe quaternion when the two-hand gesture began. */
      globeStartQuat: THREE.Quaternion
    }
  | {
      kind: 'inertia'
      /**
       * Angular velocity in world space — direction = rotation axis,
       * magnitude = radians per second. Decays exponentially each
       * frame; mode reverts to `idle` when speed drops below
       * `MIN_INERTIA_SPEED`. Cancelled instantly by any new trigger
       * press that hits the globe.
       */
      velocity: THREE.Vector3
    }

/**
 * Build a line segment extending from the controller along -Z. The
 * line is full-length (RAY_LENGTH) by default; the per-frame
 * `updateRayVisuals()` shortens it to the raycast hit distance via
 * `line.scale.z` so it visually "lands" on whatever the user is
 * pointing at, rather than poking through.
 */
function buildRayLine(THREE_: typeof THREE): THREE.Line {
  const geometry = new THREE_.BufferGeometry().setFromPoints([
    new THREE_.Vector3(0, 0, 0),
    new THREE_.Vector3(0, 0, -RAY_LENGTH),
  ])
  const material = new THREE_.LineBasicMaterial({
    color: 0x4da6ff, // --color-accent
    transparent: true,
    opacity: 0.6,
  })
  const line = new THREE_.Line(geometry, material)
  line.name = 'vr-controller-ray'
  return line
}

/**
 * Floating button-affordance label sprite that sits above the
 * controller. Sprites always face the camera, so the text stays
 * readable no matter how the user holds the controller.
 *
 * Phase 2.2 basic version: always-on labels for the three core
 * inputs, hand-tuned for Quest Touch position. Future polish
 * (fade-on-idle, mode-sensitive hints, per-controller-type
 * positioning via XR Input Profiles) is planned — see
 * VR_INVESTIGATION_PLAN.md.
 */
function buildControllerTooltip(THREE_: typeof THREE): THREE.Sprite {
  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 320
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('[VR tooltip] 2D canvas context unavailable')

  // Subtle translucent backplate — helps readability against both
  // dark VR void and bright AR passthrough backgrounds without
  // dominating the visual.
  ctx.fillStyle = 'rgba(13, 13, 18, 0.55)'
  // Rounded-rectangle shape for a polished look. Fall back to
  // plain rect if the Path2D helper is unavailable.
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath()
    ctx.roundRect(8, 8, 496, 304, 24)
    ctx.fill()
  } else {
    ctx.fillRect(8, 8, 496, 304)
  }

  // Thin accent border
  ctx.strokeStyle = 'rgba(34, 197, 94, 0.45)' // --color-accent
  ctx.lineWidth = 2
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath()
    ctx.roundRect(8, 8, 496, 304, 24)
    ctx.stroke()
  } else {
    ctx.strokeRect(8, 8, 496, 304)
  }

  // Label lines — matching the three input affordances exposed by
  // vrInteraction today.
  ctx.fillStyle = '#e8eaf0' // --color-text
  ctx.font = '500 38px system-ui, -apple-system, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(t('vr.controllerTooltip.trigger'), 256, 60)
  ctx.fillText(t('vr.controllerTooltip.grip'), 256, 128)
  ctx.fillText(t('vr.controllerTooltip.thumbstick'), 256, 196, 480)
  // AYNI: pins drop on A / X (owner, 2026-10-04), so the trigger only turns the globe.
  ctx.fillText(t('vr.controllerTooltip.pin'), 256, 264)

  const texture = new THREE_.CanvasTexture(canvas)
  texture.colorSpace = THREE_.SRGBColorSpace
  texture.minFilter = THREE_.LinearFilter
  texture.magFilter = THREE_.LinearFilter

  const material = new THREE_.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  })
  const sprite = new THREE_.Sprite(material)
  // 14 cm wide × 7 cm tall (matches 2:1 canvas aspect). Large enough
  // to read comfortably, small enough to not dominate peripheral view.
  sprite.scale.set(0.14, 0.0875, 1)
  // 13 cm above the grip — safely clear of the controller body but
  // still close enough to feel attached.
  sprite.position.set(0, 0.13, 0)
  sprite.renderOrder = 12 // above the ray dot (11)
  return sprite
}

export function createVrInteraction(
  THREE_: typeof THREE,
  ControllerModelFactory: typeof XRControllerModelFactory,
  ctx: VrInteractionContext,
): VrInteractionHandle {
  const raycaster = new THREE_.Raycaster()
  // Scratch vectors + quaternions reused across frames. The VR
  // render loop calls these helpers 90+ times/sec; allocating new
  // Vector3 / Quaternion instances every call shows up as GC churn
  // on-device. Keep a small pool and `.set()` / `.copy()` them.
  const rayOrigin = new THREE_.Vector3()
  const rayDirection = new THREE_.Vector3()
  const scratchQuat = new THREE_.Quaternion()
  const scratchVec3A = new THREE_.Vector3()
  const scratchVec3B = new THREE_.Vector3()
  const scratchVec3C = new THREE_.Vector3()
  const scratchVec3D = new THREE_.Vector3()
  const scratchMatrix4 = new THREE_.Matrix4()
  const scratchQuatB = new THREE_.Quaternion()
  /** Quaternion that computeTwoHandOrientation writes into when called per-frame. */
  const twoHandOrientationScratch = new THREE_.Quaternion()
  /** Axis scratch for updateInertia — avoids a Vector3 alloc every inertia tick. */
  const inertiaAxisScratch = new THREE_.Vector3()

  // One controller per hand. Three.js assigns indices 0 and 1; the
  // mapping to left/right is set by the platform at runtime. For MVP
  // we don't care which is which — either trigger rotates, either
  // grip exits, either thumbstick zooms.
  const controllers: THREE.XRTargetRaySpace[] = []
  const rayLines: THREE.Line[] = []
  /**
   * Per-controller "is this trigger actively rotating the globe?".
   * True while a trigger was pressed while pointing at the globe
   * and has not yet been released. Separate from HUD arming so that
   * pointing at HUD buttons doesn't disturb rotation state.
   */
  const triggersOnGlobe: boolean[] = [false, false]
  /**
   * Whether each controller's most recent selectstart hit the HUD
   * instead of the globe. We only fire a HUD action on `selectend`
   * to match the DOM `click` model (press + release on the same
   * button).
   */
  const hudArmed: (VrHudAction | null)[] = [null, null]
  /** Per-controller "trigger pressed on the Place button" flag (DOM click semantics). */
  const placeArmed: boolean[] = [false, false]
  /** Per-controller browse action armed on selectstart (DOM click semantics). */
  const browseArmed: (VrPanelAction | null)[] = [null, null]
  /** AYNI: per-controller Khipu panel press, fired on release over the same button. */
  const khipuArmed: (VrKhipuAction | null)[] = [null, null]
  const legendArmed: (VrLegendAction | null)[] = [null, null]
  /** AYNI (owner, 2026-11-04): per-controller small panel whose ✕ was pressed, fired on release over the same ✕. */
  const worldCloseArmed: (string | null)[] = [null, null]
  /** AYNI (owner, 2026-11-04): each controller's ray is on the pop-up keyboard (its stick then does nothing). */
  const rayOnKeyboard: boolean[] = [false, false]

  /**
   * Touch drag-scroll state for the browse panel. Phones have no
   * thumbstick, so the list is scrolled by pressing on it and
   * dragging. Three phases:
   *
   *   - `idle` — no drag.
   *   - `pending` — a selectstart landed on the list region; we wait
   *     to see if it becomes a drag (movement past the threshold) or
   *     a clean tap. Pending lets a tap still select a dataset.
   *   - `scroll` — movement exceeded the threshold; the gesture is a
   *     drag-scroll. Any armed card select is cancelled so dragging
   *     on a card scrolls instead of selecting.
   *
   * Controller sessions are unaffected: a controller tap is a
   * press+release with no movement, so it stays `pending` and fires
   * the select normally; controller scrolling still goes through the
   * thumbstick path.
   */
  type BrowseDragState =
    | { kind: 'idle' }
    | { kind: 'pending'; controllerIndex: 0 | 1; startUvX: number; startUvY: number }
    | { kind: 'scroll'; controllerIndex: 0 | 1 }
  let browseDrag: BrowseDragState = { kind: 'idle' }
  /**
   * Movement (in panel-UV units) before a press is treated as a drag
   * rather than a tap. ~0.02 ≈ 12 px on a 600-px-tall canvas — small
   * enough that an intentional drag registers immediately, large
   * enough that a slightly jittery tap still selects.
   */
  const BROWSE_DRAG_THRESHOLD = 0.02
  /** Per-controller "ray is currently on the browse panel" — drives thumbstick scroll. */
  const rayOnBrowse: boolean[] = [false, false]
  /** AYNI: where each controller's ray meets the side panel, for the thumbstick to scroll the list it is over. */
  const rayBrowseUv: Array<{ x: number; y: number } | null> = [null, null]

  /**
   * Date-track scrub state. Armed by a select that lands on the strip;
   * the ray is re-read every frame while armed, and the release decides
   * whether it was a scrub or a tap.
   */
  type TimelineDrag =
    | { kind: 'idle' }
    | {
        kind: 'scrub'
        controllerIndex: 0 | 1
        /** Progress where the press landed — the tap test's baseline. */
        startProgress: number
        /** Progress last seen under the ray; the release fallback. */
        lastProgress: number
      }
  let timelineDrag: TimelineDrag = { kind: 'idle' }
  /**
   * Progress units a press may drift and still count as a tap. The bar
   * spans most of the strip's width, so 0.02 is roughly 22 px on the
   * canvas — past a finger's wobble, short of a deliberate drag. It is
   * what keeps a handheld from seeking on every rotate across the strip.
   */
  const TIMELINE_TAP_SLOP = 0.02
  /** `performance.now()` of the last preview seek, rationed by
   *  `SCRUB_SEEK_INTERVAL_MS` — one constant, shared with the 2D track,
   *  because it is one decoder problem. */
  let lastScrubSeekAt = 0
  /**
   * Per-controller reference to the globe mesh that was grabbed on
   * selectstart. Passed to captureSingleMode so the surface-pinned
   * rotation math uses the correct globe center — critical when the
   * user grabs a secondary globe at a different arc position.
   */
  const lastGrabbedGlobe: (THREE.Mesh | null)[] = [null, null]
  /** AYNI: a press on a globe not yet released, with what a tap needs to be told from a grab. */
  const globeTap: Array<{ uv: THREE.Vector2; mesh: THREE.Mesh; quaternion: THREE.Quaternion; at: number; zoomVersion: number } | null> = [null, null]
  /** AYNI (satellites plan): a press on the globe or on nothing, offered as a ray on release (onRayTap). */
  const rayTap: Array<{ quaternion: THREE.Quaternion; at: number } | null> = [null, null]
  /** AYNI (Fully VR): a trigger that grabbed the sky: the ray it started on and the globe then. */
  const skyDrag: Array<{ startDir: THREE.Vector3; globeStart: THREE.Quaternion } | null> = [null, null]
  const skyDirScratch = new THREE_.Vector3()
  /** Current rotation mode — recomputed on every trigger state change. */
  let rotationMode: RotationMode = { kind: 'idle' }
  /** AYNI: globe grabs this session; the controller hints fade after a few. */
  let globeGrabs = 0

  /**
   * Grab-and-carry state. Populated on `squeezestart` when the
   * controller ray is on a globe and cleared on `squeezeend`. While
   * active, the per-frame `update` writes `globe.position` so the
   * world offset captured at grab time stays pinned under the
   * controller — position only; rotation / pinch-scale are untouched
   * and keep working alongside a carry.
   *
   * Deliberate simplifications:
   *   - The offset is measured to the PRIMARY globe's center even
   *     when the ray hit a secondary — the arc follows the primary
   *     every frame in scene.update(), so the whole layout carries
   *     rigidly together.
   *   - First grab wins: squeezing the second grip while a carry is
   *     active is ignored (no two-hand carry math).
   */
  type CarryState =
    | { kind: 'idle' }
    | {
        kind: 'carry'
        controllerIndex: 0 | 1
        /** World offset from controller origin to primary globe center, captured at grab. */
        grabOffset: THREE.Vector3
      }
  let carry: CarryState = { kind: 'idle' }

  /**
   * Running track of the globe's angular velocity during single +
   * two-hand modes. We measure the actual quaternion delta produced
   * by each frame's rotation update (rather than deriving from
   * controller motion) so the tracker is agnostic to single vs.
   * two-hand math — both feed the same source.
   *
   * On release (mode → idle), if the smoothed velocity exceeds
   * `MIN_INERTIA_SPEED`, we transition to inertia mode instead and
   * the globe keeps spinning with exponential decay. This mirrors
   * the 2D MapLibre flick-to-spin behaviour requested by testers.
   */
  const velocityTracker = {
    /** Globe quaternion at the previous tick — used to compute deltas. */
    prevQuat: new THREE_.Quaternion(),
    /** Smoothed angular velocity (axis * rad/s). */
    velocity: new THREE_.Vector3(),
    /** True while we're actively tracking; false in idle / inertia. */
    active: false,
  }

  /**
   * Update `raycaster` so its ray matches the given controller's
   * world-space pose. Returns true (always) so callers can chain.
   */
  function setRaycasterFromController(controller: THREE.XRTargetRaySpace): boolean {
    controller.getWorldPosition(rayOrigin)
    // Controllers point down their local -Z axis. Rotating the
    // unit -Z vector by the controller's world quaternion gives us
    // the forward direction in world space. Reuses scratchQuat to
    // avoid per-call allocation — this runs 90+ times/sec in VR.
    controller.getWorldQuaternion(scratchQuat)
    rayDirection.set(0, 0, -1).applyQuaternion(scratchQuat)
    raycaster.ray.origin.copy(rayOrigin)
    raycaster.ray.direction.copy(rayDirection)
    return true
  }

  /**
   * Resolve what the controller is pointing at. Order matters: the
   * HUD is drawn on top of the globe (renderOrder + depthTest:false)
   * so from the user's visual perspective it should always win ties.
   *
   * Any globe hit (primary or secondary) returns `'globe'` with the
   * mesh reference — all globes rotate in lockstep, so grabbing any
   * of them initiates the same surface-pinned drag.
   */
  function pickHit(controller: THREE.XRTargetRaySpace):
    | { kind: 'hud'; action: VrHudAction; uv: { x: number; y: number } }
    | { kind: 'browse'; action: VrPanelAction }
    | { kind: 'browse-scroll' }
    | { kind: 'khipu'; action: VrKhipuAction }
    | { kind: 'legend'; action: VrLegendAction }
    | { kind: 'keyboard' }
    | { kind: 'world-panel'; id: string; close: boolean }
    | { kind: 'timeline'; progress: number }
    | { kind: 'place-button' }
    | { kind: 'globe'; mesh: THREE.Mesh; uv: THREE.Vector2 | null; point: THREE.Vector3 }
    | null {
    setRaycasterFromController(controller)

    // AYNI (owner on Quest, 2026-11-04): the pop-up keyboard first. It stands
    // nearest, over the dock and the panels; its keys are the side panel's
    // presses, and the rest of it takes a press so nothing behind it does.
    const keyboard = ctx.browse.keyboard
    if (keyboard.isVisible()) {
      const uv = raycaster.intersectObject(keyboard.mesh, false)[0]?.uv
      if (uv) {
        const action = keyboard.hitTest({ x: uv.x, y: uv.y })
        return action ? { kind: 'browse', action } : { kind: 'keyboard' }
      }
    }

    // AYNI: only while it is shown. THREE's raycaster ignores `visible`, and
    // the dock is hidden on a phone (its controls are in the DOM overlay)
    // and during the loading scene — a tap there must not press an
    // invisible button.
    const hudHits = ctx.hud.mesh.visible ? raycaster.intersectObject(ctx.hud.mesh, false) : []
    if (hudHits.length > 0 && hudHits[0].uv) {
      const uv = { x: hudHits[0].uv.x, y: hudHits[0].uv.y }
      const action = ctx.hud.hitTest(uv)
      if (action) return { kind: 'hud', action, uv }
    }

    // Browse panel — checked before globe so the user can interact
    // with the panel even when it overlaps the globe from their angle.
    if (ctx.browse.isVisible()) {
      const browseHits = raycaster.intersectObject(ctx.browse.mesh, false)
      if (browseHits.length > 0 && browseHits[0].uv) {
        const action = ctx.browse.hitTest({
          x: browseHits[0].uv.x,
          y: browseHits[0].uv.y,
        })
        if (action) return { kind: 'browse', action }
        // Ray hit the panel but not a button/card — still counts as
        // a browse hit for scroll purposes.
        return { kind: 'browse-scroll' }
      }
    }

    // AYNI: the Khipu panel, before the globe so a press there never turns it.
    if (ctx.khipu?.isVisible()) {
      const khipuHits = raycaster.intersectObject(ctx.khipu.mesh, false)
      const uv = khipuHits[0]?.uv
      if (uv) {
        const action = ctx.khipu.hitTest({ x: uv.x, y: uv.y })
        if (action) return { kind: 'khipu', action }
      }
    }

    // AYNI: the legend's ✕, before the globe likewise.
    if (ctx.legend?.isVisible()) {
      const uv = raycaster.intersectObject(ctx.legend.mesh, false)[0]?.uv
      if (uv) {
        const action = ctx.legend.hitTest({ x: uv.x, y: uv.y })
        if (action) return { kind: 'legend', action }
      }
    }

    // AYNI (owner on Quest, 2026-11-04): a small panel with a ✕ (the
    // satellite card), before the globe and the satellites behind it.
    const panel = ctx.worldPanels?.pick(raycaster) ?? null
    if (panel) return { kind: 'world-panel', id: panel.id, close: panel.close }

    // Date track — below the HUD and above the globe in priority, so a
    // drag on the strip scrubs instead of rotating. Gated by
    // `isVisible()`, so a dataset that declares no axis costs no raycast.
    if (ctx.timeline.isVisible()) {
      const trackHits = raycaster.intersectObject(ctx.timeline.mesh, false)
      const uv = trackHits[0]?.uv
      if (uv) {
        const progress = ctx.timeline.progressAtUv({ x: uv.x, y: uv.y })
        if (progress !== null) return { kind: 'timeline', progress }
      }
    }

    // Place button (AR-only, hit-test-supported sessions). Same
    // depth-overrides + renderOrder strategy as the HUD so it
    // always wins ties with the globe behind it.
    const place = ctx.placement
    if (place && place.placeButtonMesh.visible) {
      const placeHits = raycaster.intersectObject(place.placeButtonMesh, false)
      if (placeHits.length > 0 && placeHits[0].uv) {
        const action = place.hitTestButton({
          x: placeHits[0].uv.x,
          y: placeHits[0].uv.y,
        })
        if (action) return { kind: 'place-button' }
      }
    }

    // Raycast against every globe in the layout. All globes rotate
    // in lockstep (scene.update copies the primary's quaternion to
    // all secondaries), so we don't need to distinguish which globe
    // was hit — any grab initiates the same surface-pinned rotation.
    const allGlobes = ctx.getAllGlobes()
    const globeHits = raycaster.intersectObjects(allGlobes, false)
    if (globeHits.length > 0) {
      // Keep the UV. THREE populates it for free, and it is the
      // mesh-local texture coordinate — already accounting for the
      // globe's rotation — so the dataset readout can go straight
      // from here to a lat/lon without re-deriving anything.
      //
      // AYNI: a ray leaving the sphere has hit the inner face, where
      // the picture is drawn mirrored east-west; mirror the UV with it
      // so a tap or readout from inside lands on what is shown there.
      const hit = globeHits[0]
      const uv = displayUvOfHit(hit)
      return {
        kind: 'globe',
        mesh: hit.object as THREE.Mesh,
        uv,
        point: hit.point,
      }
    }

    return null
  }

  /**
   * The sphere UV a globe hit shows: THREE's UV, mirrored east-west when the
   * ray is leaving the sphere (the inner face draws the picture mirrored).
   * Reads the raycaster's current ray.
   */
  function displayUvOfHit(hit: THREE.Intersection): THREE.Vector2 | null {
    if (!hit.uv) return null
    const uv = hit.uv.clone()
    const dir = raycaster.ray.direction
    const centre = hit.object.position
    const leaving =
      dir.x * (hit.point.x - centre.x) +
      dir.y * (hit.point.y - centre.y) +
      dir.z * (hit.point.z - centre.z) > 0
    if (leaving) uv.x = 1 - uv.x
    return uv
  }

  /** AYNI (2026-10-22): the point of the ball a controller aims at (globe frame), or null off the globes. */
  function globeDisplayDirUnder(controller: THREE.XRTargetRaySpace): Vec3 | null {
    if (!setRaycasterFromController(controller)) return null
    const hit = raycaster.intersectObjects(ctx.getAllGlobes(), false)[0]
    const uv = hit ? displayUvOfHit(hit) : null
    return uv ? uvToDisplayDir(uv) : null
  }

  /** AYNI (2026-10-22): a hit's UV as the place it shows through the zoom (taps, pins, the readout). */
  function sourceUv(uv: THREE.Vector2): THREE.Vector2 {
    const s = displayUvToSourceUv(uv)
    return new THREE_.Vector2(s.x, s.y)
  }

  /**
   * Snapshot the single-drag baseline for the given controller.
   * Raycasts the controller against the globe; if the ray misses
   * (user's aim slipped during a mode transition) we fall back to
   * `idle` and the user can re-grab. Returns the new mode.
   */
  function captureSingleMode(index: 0 | 1, targetGlobe?: THREE.Mesh): RotationMode {
    const controller = controllers[index]
    setRaycasterFromController(controller)
    // If a specific globe was identified by pickHit, raycast against
    // it directly. Otherwise fall back to all globes (e.g. during
    // mode re-evaluation after the other trigger releases).
    const globe = targetGlobe ?? ctx.globe
    const hits = targetGlobe
      ? raycaster.intersectObject(targetGlobe, false)
      : raycaster.intersectObjects(ctx.getAllGlobes(), false)
    if (hits.length === 0 || !hits[0].point) return { kind: 'idle' }
    const hitGlobe = (hits[0].object as THREE.Mesh) ?? globe
    const zoom = getFocusZoom()
    const uv = isZoomed(zoom) ? displayUvOfHit(hits[0]) : null
    return {
      kind: 'single',
      index,
      grabbedGlobe: hitGlobe,
      worldGrabDir: hits[0].point.clone().sub(hitGlobe.position).normalize(),
      globeStartQuat: ctx.globe.quaternion.clone(),
      panSource: uv ? displayToSource(zoom, uvToDisplayDir(uv)) : null,
    }
  }

  /**
   * Build the quaternion of the virtual "rigid body" the two
   * controllers define at their current poses. The frame is:
   *
   *   X axis — along the connecting axis (controller 0 → 1)
   *   Y axis — average of each controller's world +Y, projected onto
   *            the plane perpendicular to X (captures wrist rolls)
   *   Z axis — derived via cross product for right-handedness
   *
   * Fallback: if the averaged up-vector is parallel to the axis
   * (hands perfectly side-by-side with both aligned), pick a
   * perpendicular from world-up; if that too is parallel (axis
   * vertical), fall back to world-right. Fallback paths still
   * produce a stable rotation because the ambiguity is around a
   * vertical axis, which matters less for globe manipulation.
   */
  /**
   * Build a world-space orientation quaternion from the two
   * controllers' current poses. Called from updateTwoHand() every
   * frame during a two-hand gesture, so this must not allocate —
   * uses only the closure-scoped scratch vectors / quaternions /
   * matrix declared above.
   *
   * @param out Target quaternion to write into. Callers who need
   *   a persistent copy (e.g. `captureTwoHandMode` storing the
   *   start orientation) pass a fresh Quaternion; per-frame
   *   callers pass a scratch.
   */
  function computeTwoHandOrientation(out: THREE.Quaternion): THREE.Quaternion {
    // pos0, pos1 → positions of both controllers in world space.
    const pos0 = scratchVec3A
    const pos1 = scratchVec3B
    controllers[0].getWorldPosition(pos0)
    controllers[1].getWorldPosition(pos1)

    // axis = unit vector from controller 0 → controller 1.
    const axis = scratchVec3C
    axis.copy(pos1).sub(pos0).normalize()

    // pos0 / pos1 no longer needed; reuse their slots as up0 / up1
    // (each controller's local +Y in world space).
    const up0 = pos0
    const up1 = pos1
    controllers[0].getWorldQuaternion(scratchQuat)
    up0.set(0, 1, 0).applyQuaternion(scratchQuat)
    controllers[1].getWorldQuaternion(scratchQuatB)
    up1.set(0, 1, 0).applyQuaternion(scratchQuatB)

    // avgUp = mean of both controller ups — the "which way is up"
    // for the virtual rigid body, including wrist-roll input.
    const avgUp = scratchVec3D
    avgUp.copy(up0).add(up1).multiplyScalar(0.5)

    // perpUp = component of avgUp perpendicular to the axis.
    // Remove the axis-parallel projection by subtracting
    // axis * (avgUp · axis). Reuses up0 as the projection scratch.
    const projection = up0
    projection.copy(axis).multiplyScalar(avgUp.dot(axis))
    const perpUp = up1
    perpUp.copy(avgUp).sub(projection)

    // Degenerate case: avgUp was parallel to axis. Fall back on
    // cross products against world-up then world-right.
    if (perpUp.lengthSq() < 0.001) {
      const worldAxis = scratchVec3A
      worldAxis.set(0, 1, 0)
      perpUp.crossVectors(axis, worldAxis)
      if (perpUp.lengthSq() < 0.001) {
        worldAxis.set(1, 0, 0)
        perpUp.crossVectors(axis, worldAxis)
      }
    }
    perpUp.normalize()

    // forward = axis × perpUp — completes the right-handed basis.
    const forward = scratchVec3D // reuse avgUp; no longer needed
    forward.crossVectors(axis, perpUp)

    scratchMatrix4.makeBasis(axis, perpUp, forward)
    return out.setFromRotationMatrix(scratchMatrix4)
  }

  /** Snapshot the two-hand baseline. Both controllers assumed on-globe. */
  function captureTwoHandMode(): RotationMode {
    const p0 = new THREE_.Vector3()
    const p1 = new THREE_.Vector3()
    controllers[0].getWorldPosition(p0)
    controllers[1].getWorldPosition(p1)
    const startDistance = p0.distanceTo(p1)
    // Guard against a start distance too small to be a gesture — see
    // MIN_TWO_HAND_SEPARATION_M: this is what stops a phone's two
    // transient sources (both at the device) from reporting an
    // arbitrarily large pinch. Falls back to idle; the user can release
    // and re-grab.
    if (startDistance < MIN_TWO_HAND_SEPARATION_M) return { kind: 'idle' }
    return {
      kind: 'two-hand',
      // Fresh quaternion — this is stored as the two-hand baseline
      // and needs to survive beyond the next frame's scratch reuse.
      startOrientation: computeTwoHandOrientation(new THREE_.Quaternion()),
      startDistance,
      startScale: ctx.globe.scale.x,
      globeStartQuat: ctx.globe.quaternion.clone(),
    }
  }

  /**
   * Rebuild `rotationMode` from the current `triggersOnGlobe` state.
   * Idempotent for mode kinds that are already correct — avoids
   * re-snapshotting on unrelated updates (e.g. HUD arming).
   *
   * On the rotation → idle transition, kicks off `inertia` mode if
   * the user was rotating fast enough at release. New presses on
   * the globe always cancel inertia (rotationMode becomes `single`
   * or `two-hand`, replacing the inertia state).
   */
  function reevaluateRotationMode(): void {
    const wasRotating =
      rotationMode.kind === 'single' || rotationMode.kind === 'two-hand'
    // Capture the SPECIFIC kind before the assignment below
    // overwrites it — needed to tag the Tier B vr_interaction event
    // with the right gesture (drag vs pinch).
    const wasTwoHand = rotationMode.kind === 'two-hand'
    const g0 = triggersOnGlobe[0]
    const g1 = triggersOnGlobe[1]
    if (g0 && g1) {
      if (rotationMode.kind !== 'two-hand') rotationMode = captureTwoHandMode()
    } else if (g0) {
      if (!(rotationMode.kind === 'single' && rotationMode.index === 0)) {
        rotationMode = captureSingleMode(0, lastGrabbedGlobe[0] ?? undefined)
      }
    } else if (g1) {
      if (!(rotationMode.kind === 'single' && rotationMode.index === 1)) {
        rotationMode = captureSingleMode(1, lastGrabbedGlobe[1] ?? undefined)
      }
    } else {
      // Releasing the last trigger. If the velocity tracker has a
      // significant signal, transition to inertia rather than
      // idle so the spin continues with decay.
      const releaseSpeed = velocityTracker.velocity.length()
      if (wasRotating && releaseSpeed > MIN_INERTIA_SPEED) {
        rotationMode = {
          kind: 'inertia',
          velocity: velocityTracker.velocity.clone(),
        }
        // Tier B: flick-to-spin started. Magnitude is the
        // initial angular speed in rad/s — surfaces "casual
        // tap" vs "vigorous flick" without leaking head pose.
        emitVrInteraction('flick_spin', releaseSpeed)
      } else {
        rotationMode = { kind: 'idle' }
        // Transitioned straight from rotating to idle — the user let
        // go without enough velocity for inertia. That's a settle.
        // Inertia → idle settles are emitted from the per-frame
        // update loop instead (when velocity decays below threshold).
        if (wasRotating) {
          ctx.onCameraSettled?.()
          // Tier B: rotation gesture released without a flick.
          // Magnitude is the velocity-tracker speed at release
          // (rad/s) rounded to 2 decimals — small for careful
          // panning, large for fast turns.
          emitVrInteraction(wasTwoHand ? 'pinch' : 'drag', releaseSpeed)
        }
      }
    }
  }

  /**
   * Read the browse panel's UV.y at the current raycaster position.
   * The raycaster is already aimed at the controller from the
   * preceding pickHit, so this is one extra intersectObject against
   * the panel only — called on the infrequent selectstart, not per
   * frame. Returns null when the ray misses the panel (no UV).
   */
  /**
   * Axis progress under a controller's ray, or null when the ray is off
   * the strip. Raycasts the track alone rather than calling `pickHit`,
   * because a scrub re-reads this every frame and `pickHit` walks every
   * interactive surface in the session.
   */
  function timelineProgressUnder(controller: THREE.XRTargetRaySpace): number | null {
    if (!ctx.timeline.isVisible()) return null
    setRaycasterFromController(controller)
    const uv = raycaster.intersectObject(ctx.timeline.mesh, false)[0]?.uv
    if (!uv) return null
    return ctx.timeline.progressAtUv({ x: uv.x, y: uv.y })
  }

  function currentBrowseUv(): { x: number; y: number } | null {
    const hits = raycaster.intersectObject(ctx.browse.mesh, false)
    const uv = hits[0]?.uv
    return uv ? { x: uv.x, y: uv.y } : null
  }

  /**
   * Subtle haptic tick on the controller that started a gesture.
   * `hapticActuators` comes from the WebXR Gamepads module, not the
   * standard Gamepad API, so it needs the repo's `as unknown as`
   * cast pattern; everything is guarded because screen input has no
   * gamepad at all and some controllers lack actuators. Failures
   * (unsupported effect, rejected promise) are swallowed — haptics
   * are garnish, never worth an exception.
   */
  function pulseHaptic(index: 0 | 1, intensity = 0.5, durationMs = 30): void {
    const gp = inputSources[index]?.gamepad
    if (!gp) return
    const actuators = (gp as unknown as {
      hapticActuators?: ReadonlyArray<{
        pulse?: (value: number, duration: number) => unknown
      }>
    }).hapticActuators
    const pulse = actuators?.[0]?.pulse
    if (!pulse) return
    try {
      const result = pulse.call(actuators![0], intensity, durationMs)
      // `pulse` returns a Promise<boolean> on modern implementations —
      // attach a rejection handler so a haptic failure never surfaces
      // as an unhandled rejection.
      if (result && typeof (result as Promise<unknown>).catch === 'function') {
        void (result as Promise<unknown>).catch(() => { /* haptics unavailable */ })
      }
    } catch { /* haptics unavailable */ }
  }

  function onSelectStart(index: 0 | 1): void {
    const controller = controllers[index]

    // Place mode short-circuits almost everything — a trigger tap
    // anywhere confirms the placement at whatever the reticle
    // currently shows. One exception, raycast first: tapping the
    // Place BUTTON mid-flow exits Place mode without placing (the
    // documented "re-tap exits" behaviour, previously unreachable
    // because the confirm short-circuit ran before any raycast).
    // Screen-class taps route through the same transient controller
    // slot, so this covers handheld AR too — with the DOM cancel
    // button (vrPlacementTouch) as the more forgiving touch target.
    // Globe-grab is suppressed during Place mode.
    if (ctx.placement?.isPlacing()) {
      const hit = pickHit(controller)
      // AYNI (Quest review B6): in a headset a press on the dock or a
      // panel cancels Place too (every press used to confirm, so nothing
      // else worked). A phone keeps its DOM Cancel button.
      if (hit?.kind === 'place-button' || (hit && hit.kind !== 'globe' && !ctx.isScreenInput?.())) {
        ctx.onPlaceButton()
        return
      }
      pulseHaptic(index)
      ctx.onPlaceConfirm()
      return
    }

    const hit = pickHit(controller)
    rayTap[index] = !hit || hit.kind === 'globe'
      ? { quaternion: ctx.globe.quaternion.clone(), at: performance.now() }
      : null
    if (!hit) {
      // AYNI (Fully VR): a press on nothing grabs the sky (controllers only).
      if (ctx.canGrabSky?.() && !ctx.isScreenInput?.() && setRaycasterFromController(controller)) {
        skyDrag[index] = { startDir: raycaster.ray.direction.clone(), globeStart: ctx.globe.quaternion.clone() }
      }
      return
    }

    if (hit.kind === 'hud') {
      hudArmed[index] = hit.action
      return
    }

    if (hit.kind === 'browse') {
      browseArmed[index] = hit.action
      // A press on a dataset CARD may turn into a drag-scroll on
      // phones (no thumbstick). Arm the select for the tap case,
      // but also open a pending drag — if the pointer moves past
      // the threshold before release, the gesture becomes a scroll
      // and the armed select is cancelled. Category/close actions
      // are discrete taps, so they don't participate.
      if (hit.action.kind === 'browse-select') {
        const start = currentBrowseUv()
        if (start) {
          browseDrag = { kind: 'pending', controllerIndex: index, startUvX: start.x, startUvY: start.y }
        }
      }
      return
    }

    if (hit.kind === 'browse-scroll') {
      // Ray hit the panel body (not a button/card). On phones this
      // is the start of a drag-scroll; controllers never scroll this
      // way (they use the thumbstick), and a press here has no armed
      // action to fire on release, so pending is harmless for them.
      const start = currentBrowseUv()
      if (start) {
        browseDrag = { kind: 'pending', controllerIndex: index, startUvX: start.x, startUvY: start.y }
      }
      return
    }

    if (hit.kind === 'khipu') {
      khipuArmed[index] = hit.action
      return
    }

    if (hit.kind === 'legend') {
      legendArmed[index] = hit.action
      return
    }

    // AYNI (owner, 2026-11-04): the keyboard's body and a small panel's body take the press and do nothing.
    if (hit.kind === 'keyboard') return
    if (hit.kind === 'world-panel') {
      worldCloseArmed[index] = hit.close ? hit.id : null
      return
    }

    if (hit.kind === 'timeline') {
      // Scrub the date axis. A headset gets a live preview from the first
      // frame; a phone waits for the release, because the same drag is
      // also rotating the globe and only a tap should seek.
      timelineDrag = {
        kind: 'scrub',
        controllerIndex: index,
        startProgress: hit.progress,
        lastProgress: hit.progress,
      }
      if (!ctx.isScreenInput?.()) {
        lastScrubSeekAt = performance.now()
        ctx.onTimelineSeek?.(hit.progress, 'preview')
      }
      return
    }

    if (hit.kind === 'place-button') {
      placeArmed[index] = true
      return
    }

    // Handheld-AR rotation is DOM-driven, not XR-driven: the rotate
    // layer (vrRotateTouch) spins the globe from the real touch point,
    // so the transient screen ray must NOT also grab-and-rotate or one
    // drag would turn the globe twice. Every branch above still runs,
    // so HUD / browse / Khipu / Place-button taps keep working exactly as
    // they did; only the globe grab is declined on this device.
    // AYNI: a press on the globe may be a tap on a place (a radio
    // station, a cam): remembered here, decided on release, on every
    // device — a phone's tap arrives through this same path before its
    // rotate layer declines the grab below.
    globeTap[index] = hit.uv
      ? { uv: sourceUv(hit.uv), mesh: hit.mesh, quaternion: ctx.globe.quaternion.clone(), at: performance.now(), zoomVersion: focusZoomVersion() }
      : null

    if (ctx.domTouchActive?.()) return

    // Any globe hit (primary or secondary) — flip this trigger's
    // rotation bit and capture which globe was grabbed so the
    // surface-pinned drag math uses the right center point.
    // All globes rotate in lockstep (scene.update copies the
    // primary's quaternion), so grabbing any globe works the same.
    lastGrabbedGlobe[index] = hit.mesh
    triggersOnGlobe[index] = true
    pulseHaptic(index) // grab-rotate start feedback
    globeGrabs++
    reevaluateRotationMode()
  }

  function onSelectEnd(index: 0 | 1): void {
    skyDrag[index] = null
    // AYNI (satellites plan): a quick press that turned nothing may have
    // aimed at a satellite, on or off the globe; it goes first.
    const ray = rayTap[index]
    rayTap[index] = null
    if (
      ray &&
      ctx.onRayTap &&
      performance.now() - ray.at <= GLOBE_TAP_MAX_MS &&
      ctx.globe.quaternion.angleTo(ray.quaternion) <= GLOBE_TAP_MAX_TURN_RAD &&
      setRaycasterFromController(controllers[index]) &&
      ctx.onRayTap(raycaster.ray.origin, raycaster.ray.direction)
    ) {
      globeTap[index] = null
    }
    // A press on the globe released soon, without turning it, is a tap
    // on the place under it. A grab that rotated is not.
    const tap = globeTap[index]
    if (tap) {
      globeTap[index] = null
      const held = performance.now() - tap.at
      const turned = ctx.globe.quaternion.angleTo(tap.quaternion)
      // AYNI: a drag that moved the zoomed map is not a tap either.
      const panned = focusZoomVersion() !== tap.zoomVersion
      if (held <= GLOBE_TAP_MAX_MS && turned <= GLOBE_TAP_MAX_TURN_RAD && !panned) {
        ctx.onGlobeTap?.(tap.uv, tap.mesh)
      }
    }
    // HUD actions fire on release (mirrors DOM click semantics).
    if (hudArmed[index]) {
      const controller = controllers[index]
      const hit = pickHit(controller)
      // Only fire if the user is still pointing at the same action
      // as when they pressed — otherwise they slid off the button
      // and cancelled.
      if (hit?.kind === 'hud' && hit.action === hudArmed[index]) {
        ctx.onHudAction(hudArmed[index]!, hit.uv)
        // Tier B: HUD button activated. Magnitude is always 1 —
        // the event is presence/absence, not intensity.
        emitVrInteraction('hud_tap', 1)
      }
      hudArmed[index] = null
    }
    // Browse panel: same click semantics — fire only if still pointing
    // at the same action on release.
    if (browseArmed[index]) {
      const controller = controllers[index]
      const hit = pickHit(controller)
      if (hit?.kind === 'browse') {
        const armed = browseArmed[index]!
        if (panelActionsEqual(armed, hit.action)) {
          ctx.onBrowseAction(hit.action)
        }
      }
      browseArmed[index] = null
    }
    // Date track release. A headset commits whatever the drag reached; a
    // handheld commits only a tap, so a rotate that crossed the strip does
    // not also move the series. The release ray wins over the last preview
    // so a drag that left the bar still lands where the user let go, with
    // the last-seen progress as the fallback.
    if (timelineDrag.kind === 'scrub' && timelineDrag.controllerIndex === index) {
      const released =
        timelineProgressUnder(controllers[index]) ?? timelineDrag.lastProgress
      const isTap =
        Math.abs(released - timelineDrag.startProgress) <= TIMELINE_TAP_SLOP
      if (isTap || !ctx.isScreenInput?.()) {
        ctx.onTimelineSeek?.(released, 'commit')
      }
      timelineDrag = { kind: 'idle' }
    }
    // AYNI: the Khipu panel, with the same press-and-release-on-same-button semantics.
    if (khipuArmed[index]) {
      const hit = pickHit(controllers[index])
      if (hit?.kind === 'khipu' && hit.action === khipuArmed[index]) ctx.onKhipuAction?.(khipuArmed[index]!)
      khipuArmed[index] = null
    }
    if (legendArmed[index]) {
      const hit = pickHit(controllers[index])
      if (hit?.kind === 'legend' && hit.action === legendArmed[index]) ctx.onLegendAction?.(legendArmed[index]!)
      legendArmed[index] = null
    }
    if (worldCloseArmed[index]) {
      const id = worldCloseArmed[index]!
      worldCloseArmed[index] = null
      const hit = pickHit(controllers[index])
      if (hit?.kind === 'world-panel' && hit.close && hit.id === id) ctx.onWorldPanelClose?.(id)
    }
    // Browse drag-scroll end. A 'pending' drag (no movement) was a
    // tap — fall through to the armed-select handling above so the
    // card still loads. A 'scroll' drag already cancelled the armed
    // select on promotion, so there's nothing to fire; just clear.
    if (browseDrag.kind !== 'idle' && browseDrag.controllerIndex === index) {
      browseDrag = { kind: 'idle' }
    }
    // Place button: same press-and-release-on-same-target click semantics.
    if (placeArmed[index]) {
      const controller = controllers[index]
      const hit = pickHit(controller)
      if (hit?.kind === 'place-button') {
        ctx.onPlaceButton()
      }
      placeArmed[index] = false
    }
    if (triggersOnGlobe[index]) {
      triggersOnGlobe[index] = false
      lastGrabbedGlobe[index] = null
      reevaluateRotationMode()
    }
  }

  function onSqueezeStart(index: 0 | 1): void {
    // Grip on the globe = grab-and-carry. Suppressed during Place
    // mode — the placement flow owns globe positioning there (its
    // own trigger handling confirms the spot), and a carry mid-place
    // would fight the reticle / height rail.
    const placing = ctx.placement?.isPlacing() ?? false
    // AYNI (owner, 2026-11-04): nor from inside a globe grown past 2 m (canCarry).
    if (!placing && carry.kind === 'idle' && (ctx.canCarry?.() ?? true)) {
      const hit = pickHit(controllers[index])
      if (hit?.kind === 'globe') {
        const controller = controllers[index]
        controller.getWorldPosition(scratchVec3A)
        carry = {
          kind: 'carry',
          controllerIndex: index,
          grabOffset: ctx.globe.position.clone().sub(scratchVec3A),
        }
        ctx.onCarryStart?.()
        pulseHaptic(index) // grab-and-carry start feedback
        return
      }
    }
    // AYNI (Quest review B1, owner 2026-11-03): a grip anywhere else does
    // nothing. It used to arm an 800 ms exit with no warning, and
    // beginners keep the grip squeezed while holding the controller.
    // Exit is on the dock, and the headset's own button always leaves.
  }

  function onSqueezeEnd(index: 0 | 1): void {
    if (carry.kind === 'carry' && carry.controllerIndex === index) {
      carry = { kind: 'idle' }
      // Moving the globe changes the view distance / framing — the
      // same "settled" signal the other manipulation gestures fire.
      ctx.onCameraSettled?.()
    }
  }

  // --- Wire up both controllers ---

  // One factory shared between both controllers; the addon caches
  // model fetches internally so each controller doesn't re-download.
  const modelFactory = new ControllerModelFactory()
  // AYNI (Quest review B9): the models come from our own host, not
  // jsdelivr (school networks often block outside CDNs); see
  // public/xr-input-profiles/README.md.
  modelFactory.setPath(XR_INPUT_PROFILES_PATH)

  // Per-controller grip spaces (where the controller body sits) and
  // small white dots placed at the raycast hit point each frame.
  // Grip space is distinct from the target-ray space (`getController`)
  // — the ray fires from a sensible "pointer" origin while the grip
  // tracks the physical controller body, which is what we want to
  // attach the visual model to.
  const grips: THREE.XRGripSpace[] = []
  const dots: THREE.Mesh[] = []
  // Button-affordance tooltip sprites attached to each grip — tracked
  // separately so we can dispose their canvases and materials.
  const tooltips: THREE.Sprite[] = []

  for (const i of [0, 1] as const) {
    const controller = ctx.renderer.xr.getController(i) as THREE.XRTargetRaySpace
    controllers.push(controller)
    const ray = buildRayLine(THREE_)
    rayLines.push(ray)
    controller.add(ray)
    ctx.scene.add(controller)

    // Controller model — auto-detects which Quest controller (Touch,
    // Touch Pro, etc.) and downloads the matching glTF from the
    // WebXR Input Profiles CDN. The model animates trigger pulls
    // and button presses automatically.
    const grip = ctx.renderer.xr.getControllerGrip(i) as THREE.XRGripSpace
    grip.add(modelFactory.createControllerModel(grip))
    ctx.scene.add(grip)
    grips.push(grip)

    // Button-affordance tooltip floating above each controller.
    // Shown on BOTH controllers for symmetry — either hand provides
    // the same hint regardless of dominance.
    const tooltip = buildControllerTooltip(THREE_)
    grip.add(tooltip)
    tooltips.push(tooltip)

    // Intersection dot — small white sphere positioned at the
    // raycast hit point each frame. Hidden when the ray misses.
    // Disable depth test so the dot never gets clipped behind the
    // surface it's resting on (robust against floating-point z-fight).
    const dotGeometry = new THREE_.SphereGeometry(0.008, 12, 12)
    const dotMaterial = new THREE_.MeshBasicMaterial({
      color: 0xffffff,
      depthTest: false,
    })
    const dot = new THREE_.Mesh(dotGeometry, dotMaterial)
    dot.renderOrder = 11 // above HUD (which is 10)
    dot.visible = false
    ctx.scene.add(dot)
    dots.push(dot)

    // Bind captured index so the shared handlers know which slot.
    controller.addEventListener('selectstart', () => onSelectStart(i))
    controller.addEventListener('selectend', () => onSelectEnd(i))
    controller.addEventListener('squeezestart', () => onSqueezeStart(i))
    controller.addEventListener('squeezeend', () => onSqueezeEnd(i))

    // Stash this controller's XRInputSource on `connected`. Needed
    // by updateThumbstickZoom, which reads the gamepad — iterating
    // `session.inputSources` directly would conflate controller
    // indices with transient-pointer / gaze sources (which don't
    // have gamepads but still occupy an index in the list), causing
    // the rayOnBrowse lookup to land on the wrong hand.
    controller.addEventListener('connected', (ev: { data: XRInputSource }) => {
      inputSources[i] = ev.data
    })
    controller.addEventListener('disconnected', () => {
      inputSources[i] = null
    })
  }

  /**
   * Per-controller XRInputSource, populated on `connected` /
   * cleared on `disconnected`. Index matches `controllers[i]`, so
   * inputSources[0] is always the left-hand (or first-connected)
   * controller — stable across the session even when transient-
   * pointer / gaze sources come and go.
   */
  const inputSources: (XRInputSource | null)[] = [null, null]

  // AYNI (Pico + Wolvic, 2026-11-03): three ties an input source to a
  // controller only inside its `inputsourceschange` handler, and only to a
  // controller that already exists (`getController` above). The session is
  // bound to the renderer long before this runs — the controller-model
  // import, the scene, the panels and, in AR, the reference-space, hit-test
  // and anchor requests come in between, and three's frame loop is already
  // running — so a browser that reports its controllers in the first frames
  // had them dropped for the whole session: no rays, no buttons, no models.
  // The IWER emulator does exactly that. Announce once, now that the
  // controllers and their listeners exist, any source this setup has not
  // seen; three assigns it as it would have and fires `connected`.
  replayUnseenInputSources(ctx.renderer.xr.getSession(), inputSources)

  // Scratch vectors + quaternion reused every frame; avoids
  // allocation in the XR hot path.
  const currentWorldDir = new THREE_.Vector3()
  const deltaQ = new THREE_.Quaternion()
  const p0 = new THREE_.Vector3()
  const p1 = new THREE_.Vector3()

  /**
   * Scale the rotation a quaternion represents by `factor`, in place.
   * `q` is interpreted as a rotation by angle θ about some axis;
   * after the call it represents a rotation by `θ * factor` about
   * the same axis, clamped so the final angle stays within ±π
   * (Three.js wraps beyond that anyway, but explicit clamping
   * avoids the visible "snap" wraparound).
   */
  function scaleRotationAngle(q: THREE.Quaternion, factor: number): void {
    // Normalize to the shortest-path representation (w ≥ 0). Both q
    // and -q encode the same rotation, but axis-angle decomposition
    // requires the canonical form.
    if (q.w < 0) {
      q.x = -q.x
      q.y = -q.y
      q.z = -q.z
      q.w = -q.w
    }
    const w = Math.min(1, q.w)
    const angle = 2 * Math.acos(w)
    if (angle < 1e-6) return
    const sinHalf = Math.sin(angle / 2)
    const ax = q.x / sinHalf
    const ay = q.y / sinHalf
    const az = q.z / sinHalf
    const newAngle = Math.min(Math.PI, angle * factor)
    const halfNew = newAngle / 2
    const sinHalfNew = Math.sin(halfNew)
    q.set(ax * sinHalfNew, ay * sinHalfNew, az * sinHalfNew, Math.cos(halfNew))
  }

  /** Apply the surface-pinned rotation for the active single-drag. */
  function updateSingle(mode: Extract<RotationMode, { kind: 'single' }>): void {
    // Guard: the grabbed globe may have been disposed mid-frame if
    // the 2D app dropped from a 2-globe to a 1-globe layout while
    // the user had trigger held — scene.setPanelCount pops and
    // disposes the secondary, leaving our captured reference
    // dangling. Raycasting against a disposed mesh would throw or
    // return garbage; cancel the mode back to idle and let the next
    // frame re-capture on the current geometry.
    if (!ctx.getAllGlobes().includes(mode.grabbedGlobe)) {
      rotationMode = { kind: 'idle' }
      return
    }
    const controller = controllers[mode.index]
    setRaycasterFromController(controller)
    // Raycast against the specific globe the user grabbed so the
    // surface-pinned math stays correct even when the grabbed globe
    // is a secondary at a different arc position than the primary.
    const hits = raycaster.intersectObject(mode.grabbedGlobe, false)
    // Ray swung off-globe — freeze rotation until it lands back on
    // the surface. Matches 2D mouse-off-canvas semantics.
    if (hits.length === 0 || !hits[0].point) return
    // AYNI (2026-10-22): zoomed, the grabbed place follows the ray across
    // the magnified map; the globe itself stays put (vrFocusZoom.ts).
    if (mode.panSource) {
      const zoom = getFocusZoom()
      if (!isZoomed(zoom)) {
        rotationMode = captureSingleMode(mode.index, mode.grabbedGlobe)
        return
      }
      const uv = displayUvOfHit(hits[0])
      if (uv) {
        panTo(zoom, mode.panSource, uvToDisplayDir(uv))
        commitFocusZoom()
      }
      return
    }
    currentWorldDir.copy(hits[0].point).sub(mode.grabbedGlobe.position).normalize()
    deltaQ.setFromUnitVectors(mode.worldGrabDir, currentWorldDir)
    scaleRotationAngle(deltaQ, ROTATION_SENSITIVITY)
    // Always write to the PRIMARY globe's quaternion — scene.update()
    // copies it to all secondaries, so the lockstep stays intact.
    ctx.globe.quaternion.copy(mode.globeStartQuat).premultiply(deltaQ)
  }

  /** Apply pinch-scale + full rigid-body rotation for the two-hand gesture. */
  function updateTwoHand(mode: Extract<RotationMode, { kind: 'two-hand' }>): void {
    controllers[0].getWorldPosition(p0)
    controllers[1].getWorldPosition(p1)
    const currentDistance = p0.distanceTo(p1)
    // Degenerate mid-gesture (hands coincident) — freeze to avoid a
    // divide-by-zero-ish blow-up.
    if (currentDistance < 0.001) return

    // Pinch zoom: scale is proportional to the distance ratio,
    // clamped to the globe's configured min/max. SUPPRESSED on any
    // touch-only session: two simultaneous touches register as two
    // transient input sources whose world positions sit at the device,
    // so the "distance" between them is meaningless and the ratio runs
    // away — the globe jumps to MAX_GLOBE_SCALE on contact and never
    // comes back down. Real controllers carry a gamepad and are tens of
    // centimetres apart, so they keep this path. Rigid-body rotation
    // below still applies (two-finger twist rotates).
    if (!ctx.isScreenInput?.()) {
      const scale = Math.max(
        MIN_GLOBE_SCALE,
        Math.min(MAX_GLOBE_SCALE, mode.startScale * (currentDistance / mode.startDistance)),
      )
      ctx.globe.scale.setScalar(scale)
    }

    // Rotation: full rigid-body delta. Earlier version only
    // considered the axis direction between the hands, so wrist
    // twists produced no globe rotation — users reported that
    // rotation "didn't keep up with the controllers". Capturing
    // the average up-vector inside the two-hand orientation picks
    // up wrist rolls too.
    // Writes into scratch — no allocation, per-frame safe.
    const currentOrientation = computeTwoHandOrientation(twoHandOrientationScratch)
    // delta = current * start⁻¹, then globe = delta * globeStart
    deltaQ.copy(mode.startOrientation).invert().premultiply(currentOrientation)
    scaleRotationAngle(deltaQ, ROTATION_SENSITIVITY)
    ctx.globe.quaternion.copy(mode.globeStartQuat).premultiply(deltaQ)
  }

  /**
   * Continue the decaying spin from a release-time velocity. Stops
   * (transitions to idle) when speed drops below `MIN_INERTIA_SPEED`
   * so we don't burn frames applying invisible rotations.
   */
  function updateInertia(
    mode: Extract<RotationMode, { kind: 'inertia' }>,
    deltaSeconds: number,
  ): void {
    // Exponential decay of velocity over wall-clock time.
    const decay = Math.exp(-deltaSeconds / INERTIA_TIME_CONSTANT)
    mode.velocity.multiplyScalar(decay)
    const speed = mode.velocity.length()
    if (speed < MIN_INERTIA_SPEED) {
      rotationMode = { kind: 'idle' }
      // Flick-inertia has decayed to a stop — the globe is now
      // settled at wherever the spin left it.
      ctx.onCameraSettled?.()
      return
    }
    const angle = speed * deltaSeconds
    // Normalize into a scratch rather than cloning the velocity —
    // this function runs every frame during a flick-to-spin, and
    // mode.velocity is a mutable vector we can't normalize in-place
    // (that would change its magnitude, which encodes speed).
    inertiaAxisScratch.copy(mode.velocity).normalize()
    deltaQ.setFromAxisAngle(inertiaAxisScratch, angle)
    ctx.globe.quaternion.premultiply(deltaQ)
  }

  /** Quaternion scratch reused for velocity-delta math. */
  const velDelta = new THREE_.Quaternion()
  const velPrevInverse = new THREE_.Quaternion()
  const velSampleAxis = new THREE_.Vector3()
  const velSample = new THREE_.Vector3()

  /**
   * Track the globe's angular velocity each frame during single +
   * two-hand modes. Activates on first frame of rotation, deactivates
   * outside those modes. Smoothes via `lerp` so a single jittery
   * frame doesn't poison the velocity captured at release.
   */
  function updateVelocityTracking(deltaSeconds: number, isRotating: boolean): void {
    if (!isRotating) {
      velocityTracker.active = false
      return
    }
    if (!velocityTracker.active) {
      // First frame of a fresh rotation — initialize, no sample yet.
      velocityTracker.prevQuat.copy(ctx.globe.quaternion)
      velocityTracker.velocity.set(0, 0, 0)
      velocityTracker.active = true
      return
    }
    if (deltaSeconds <= 0) return

    // delta = current * prev⁻¹  (rotation that takes prev → current).
    velPrevInverse.copy(velocityTracker.prevQuat).invert()
    velDelta.copy(ctx.globe.quaternion).multiply(velPrevInverse)

    // Convert delta quaternion to angular velocity vector. Take the
    // shortest-path interpretation (flip sign if w < 0).
    const w = Math.max(-1, Math.min(1, velDelta.w < 0 ? -velDelta.w : velDelta.w))
    const sign = velDelta.w < 0 ? -1 : 1
    const angle = 2 * Math.acos(w)
    if (angle > 1e-5) {
      const sinHalf = Math.sin(angle / 2)
      velSampleAxis.set(velDelta.x, velDelta.y, velDelta.z).multiplyScalar(sign / sinHalf)
      velSample.copy(velSampleAxis).multiplyScalar(angle / deltaSeconds)
    } else {
      velSample.set(0, 0, 0)
    }
    velocityTracker.velocity.lerp(velSample, VELOCITY_SMOOTHING_ALPHA)
    velocityTracker.prevQuat.copy(ctx.globe.quaternion)
  }

  /**
   * Per-frame: position the laser dot at each controller's raycast
   * intersection point and shorten the ray line so it visually
   * "lands" on the surface instead of poking through. Hides the dot
   * + restores full-length ray when the ray misses everything.
   *
   * Raycasts against the globe, HUD, AND the AR Place button
   * (when it's visible). Keeping this target list in sync with
   * `pickHit`'s list is important — if the ray visuals don't land
   * on the same surfaces `pickHit` considers interactive, aiming
   * at buttons feels broken. Run after rotation updates so the dot
   * reflects the current globe orientation.
   */
  const rayTargets: THREE.Object3D[] = []
  /**
   * One-shot suppression for the updateRayVisuals try/catch. First
   * exception logs; subsequent exceptions in the same session stay
   * silent to avoid spamming the Quest browser console at 72–90 Hz
   * if something unexpectedly goes persistent.
   */
  let rayVisualErrorLogged = false
  function updateRayVisuals(): void {
    // Reuse one closure-scoped array, clear-and-push each frame so
    // we don't allocate at XR frame rate. Includes every globe in
    // the layout (so the dot snaps to secondaries), the HUD, the
    // browse panel (when visible), and the AR Place button (when
    // visible).
    rayTargets.length = 0
    const allGlobes = ctx.getAllGlobes()
    for (let i = 0; i < allGlobes.length; i++) rayTargets.push(allGlobes[i])
    if (ctx.hud.mesh.visible) rayTargets.push(ctx.hud.mesh)
    if (ctx.browse.isVisible()) {
      rayTargets.push(ctx.browse.mesh)
    }
    if (ctx.khipu?.isVisible()) rayTargets.push(ctx.khipu.mesh)
    if (ctx.legend?.isVisible()) rayTargets.push(ctx.legend.mesh)
    if (ctx.placement && ctx.placement.placeButtonMesh.visible) {
      rayTargets.push(ctx.placement.placeButtonMesh)
    }
    // AYNI (owner, 2026-11-04): the pop-up keyboard and the small panels with a ✕.
    const keyboardMesh = ctx.browse.keyboard.isVisible() ? ctx.browse.keyboard.mesh : null
    if (keyboardMesh) rayTargets.push(keyboardMesh)
    ctx.worldPanels?.pressable(rayTargets)
    for (let i = 0; i < 2; i++) {
      const controller = controllers[i]
      setRaycasterFromController(controller)
      // Closest hit across all interactive surfaces wins for the
      // ray-dot position + line length.
      const hits = raycaster.intersectObjects(rayTargets, false)
      if (hits.length > 0 && hits[0].point) {
        const distance = hits[0].distance
        // Scale Z so the line ends exactly at the hit. Min clamp
        // avoids a degenerate zero-length scale on extreme close-up.
        rayLines[i].scale.z = Math.max(0.001, distance / RAY_LENGTH)
        dots[i].position.copy(hits[0].point)
        dots[i].visible = true
      } else {
        rayLines[i].scale.z = 1
        dots[i].visible = false
      }
      // rayOnBrowse drives thumbstick-Y → scroll (vs. zoom). Match
      // `pickHit`'s priority: the browse panel wins over the globe
      // even if the globe is closer to the camera, so we check
      // whether browse.mesh is anywhere in the hit list, not just
      // at position 0. Without this, a ray that crosses both the
      // panel and a globe behind it would scroll-route incorrectly
      // (closest hit is globe → rayOnBrowse false → zoom instead of
      // scroll, mismatching the click-route above).
      const browseMesh = ctx.browse.mesh
      let onBrowse = false
      rayBrowseUv[i] = null
      for (let h = 0; h < hits.length; h++) {
        if (hits[h].object === browseMesh) {
          onBrowse = true
          const uv = hits[h].uv
          rayBrowseUv[i] = uv ? { x: uv.x, y: uv.y } : null
          break
        }
      }
      rayOnBrowse[i] = onBrowse
      rayOnKeyboard[i] = keyboardMesh !== null && hits.some((h) => h.object === keyboardMesh)
    }
  }

  /** Poll thumbstick Y across controllers and scale globe accordingly. */
  /** Scroll speed for the browse panel: canvas pixels per second at full thumbstick deflection. */
  const BROWSE_SCROLL_SPEED = 1400

  function updateThumbstickZoom(deltaSeconds: number): void {
    // Each Quest controller maps axes [2, 3] to the thumbstick
    // (axes [0, 1] are the touchpad, which the Quest doesn't
    // have). We iterate our two controllers directly using the
    // stashed inputSources array rather than `session.inputSources`:
    // the former is indexed 1:1 with `controllers[i]` / `rayOnBrowse[i]`,
    // the latter can interleave transient-pointer / gaze sources
    // that throw off index-based lookups.
    //
    // Two gates, coarse then fine. The session gate stands a handheld
    // down entirely — its only sizing control is the DOM zoom slider and
    // its browse panel scrolls by drag (browseDrag), not by an axis —
    // and `thumbstickAxisY` then refuses any individual source that
    // cannot carry a stick, which is the part that cannot be fooled by a
    // device classification. Chrome on Android hands the screen-tap
    // input source a gamepad whose axes carry the TOUCH POSITION; read
    // as a thumbstick it resized the globe on every press.
    if (ctx.isScreenInput?.()) return
    const session = ctx.renderer.xr.getSession()
    if (!session) return
    let zoomAxis = 0
    // AYNI (owner, 2026-10-22): the right stick zooms in where it points.
    let focusAxis = 0
    let focusFrom: 0 | 1 = 1
    let scrollAxis = 0
    let scrollFrom: 0 | 1 = 0
    for (let i = 0; i < 2; i++) {
      const y = thumbstickAxisY(inputSources[i])
      if (y === null) continue
      if (Math.abs(y) <= THUMBSTICK_DEADZONE) continue
      // AYNI (owner, 2026-11-04): aimed at the pop-up keyboard, a stick neither sizes, zooms nor scrolls.
      if (rayOnKeyboard[i]) continue
      // When this controller's ray is on the browse panel, redirect
      // its Y axis to scroll instead of zoom.
      if (rayOnBrowse[i]) {
        if (Math.abs(y) > Math.abs(scrollAxis)) {
          scrollAxis = y
          scrollFrom = i as 0 | 1
        }
      } else {
        // Invert so pushing up → bigger / closer. The left stick sets the
        // size, the right one zooms (stickRole).
        const signed = -y
        if (stickRole(inputSources[i]) === 'zoom') {
          if (Math.abs(signed) > Math.abs(focusAxis)) {
            focusAxis = signed
            focusFrom = i as 0 | 1
          }
        } else if (Math.abs(signed) > Math.abs(zoomAxis)) {
          zoomAxis = signed
        }
      }
    }
    if (zoomAxis !== 0) {
      // Capture the start-of-gesture scale on the rising edge so
      // the matching release can report a magnitude that means
      // "scale ratio over the whole gesture", not "ratio for the
      // last frame". Approximate log-ratio is the privacy-friendly
      // choice — small fractional values for small zooms,
      // ±1 / ±2 for an order-of-magnitude change.
      if (!wasZoomingThumbstick) {
        zoomGestureStartScale = ctx.globe.scale.x
      }
      const factor = Math.pow(ZOOM_RATE_PER_SECOND, zoomAxis * deltaSeconds)
      const next = ctx.globe.scale.x * factor
      const clamped = Math.max(MIN_GLOBE_SCALE, Math.min(MAX_GLOBE_SCALE, next))
      ctx.globe.scale.setScalar(clamped)
      wasZoomingThumbstick = true
    } else if (wasZoomingThumbstick) {
      // Thumbstick was pushing zoom last frame and returned to
      // neutral this frame — the user let go and the globe scale
      // is now settled.
      wasZoomingThumbstick = false
      ctx.onCameraSettled?.()
      // Tier B: thumbstick zoom released. Magnitude is the log2
      // ratio over the whole gesture: +1 = doubled, -1 = halved,
      // 0 = unchanged. Bounded by the MIN/MAX globe scale clamp
      // so it can't run away.
      const endScale = ctx.globe.scale.x
      if (zoomGestureStartScale > 0 && endScale > 0) {
        const log2Ratio = Math.log2(endScale / zoomGestureStartScale)
        emitVrInteraction('thumbstick_zoom', log2Ratio)
      }
      zoomGestureStartScale = 0
    }
    if (focusAxis !== 0) {
      const fold = zoomBy(
        getFocusZoom(),
        Math.pow(FOCUS_ZOOM_RATE_PER_SECOND, focusAxis * deltaSeconds),
        globeDisplayDirUnder(controllers[focusFrom]),
        ctx.focusZoomCeiling?.() ?? MAX_FOCUS_MAGNIFICATION,
      )
      if (fold) foldIntoGlobe(fold)
      commitFocusZoom()
      wasFocusZooming = true
    } else if (wasFocusZooming) {
      wasFocusZooming = false
      ctx.onCameraSettled?.()
    }
    if (scrollAxis !== 0) {
      // AYNI: squared, so a light push moves a little and a full one moves
      // fast through a long catalogue; only the list under the ray scrolls.
      const speed = Math.sign(scrollAxis) * scrollAxis * scrollAxis * BROWSE_SCROLL_SPEED
      ctx.browse.scroll(speed * deltaSeconds, rayBrowseUv[scrollFrom])
    }
  }
  /**
   * AYNI: the controller hints fade once they have done their job — after
   * TOOLTIP_SHOW_MS, or once the globe has been grabbed TOOLTIP_GRABS
   * times — and never show on a tracked hand, which has no buttons to
   * label. The first version kept them on for the whole session.
   */
  const sessionStartedAt = performance.now()
  function updateTooltips(): void {
    const elapsed = performance.now() - sessionStartedAt
    const target = elapsed > TOOLTIP_SHOW_MS || globeGrabs >= TOOLTIP_GRABS ? 0 : 1
    for (let i = 0; i < tooltips.length; i++) {
      const sprite = tooltips[i]!
      const material = sprite.material as THREE.SpriteMaterial
      const hand = !!(inputSources[i] as (XRInputSource & { hand?: unknown }) | null)?.hand
      const want = hand ? 0 : target
      material.opacity += (want - material.opacity) * 0.08
      if (Math.abs(material.opacity - want) < 0.01) material.opacity = want
      sprite.visible = material.opacity > 0.01
    }
  }
  /**
   * AYNI: A (right) and X (left) are `buttons[4]` of an xr-standard
   * gamepad. A press, on its rising edge, drops a pin where that
   * controller's ray meets a globe. Never on a phone, whose screen-tap
   * source carries a gamepad of touch positions.
   */
  const pinButtonDown: boolean[] = [false, false]
  function updatePinButtons(): void {
    if (ctx.isScreenInput?.() || !ctx.onGlobePin) return
    for (let i = 0; i < 2; i++) {
      const pressed = !!inputSources[i]?.gamepad?.buttons?.[PIN_BUTTON_INDEX]?.pressed
      if (pressed && !pinButtonDown[i]) {
        const hit = pickHit(controllers[i]!)
        if (hit?.kind === 'globe' && hit.uv) {
          pulseHaptic(i as 0 | 1, 0.7, 40)
          ctx.onGlobePin(sourceUv(hit.uv))
        }
      }
      pinButtonDown[i] = pressed
    }
  }
  /** Edge-triggered flag for thumbstick-zoom settle. Set true while
   * zoom axis is non-zero; flipping back to false fires one
   * `onCameraSettled` callback. */
  let wasZoomingThumbstick = false
  /** Globe scale at the moment the user started a thumbstick-zoom
   * gesture. Captured on the rising edge so the matching release
   * can report a magnitude that means "ratio over the whole
   * gesture", not "ratio for the last frame". */
  let zoomGestureStartScale = 0
  /** AYNI (2026-10-22): the right stick was zooming last frame. */
  let wasFocusZooming = false
  /**
   * AYNI (2026-10-22): the zoom came back to 1×: its map rotation becomes
   * the globe's own, so nothing moves (vrFocusZoom.zoomBy).
   */
  function foldIntoGlobe(fold: Quat): void {
    ctx.globe.quaternion.multiply(foldScratch.set(fold[0], fold[1], fold[2], fold[3]))
  }
  const foldScratch = new THREE_.Quaternion()

  return {
    isGrabbing() {
      return rotationMode.kind !== 'idle'
    },
    isCarrying() {
      return carry.kind === 'carry'
    },
    globeHoverUv() {
      for (const controller of controllers) {
        if (!controller) continue
        const hit = pickHit(controller)
        if (hit?.kind === 'globe' && hit.uv) return displayUvToSourceUv(hit.uv)
      }
      return null
    },
    globeHover() {
      for (const controller of controllers) {
        if (!controller) continue
        const hit = pickHit(controller)
        if (hit?.kind === 'globe' && hit.uv) return { uv: displayUvToSourceUv(hit.uv), point: hit.point }
      }
      return null
    },
    update(deltaSeconds) {
      // Date track: re-read the ray while a scrub is armed and push a
      // rationed preview, so the drawn playhead follows the controller
      // even though the decoder can only be steered a few times a second.
      if (timelineDrag.kind === 'scrub' && !ctx.isScreenInput?.()) {
        const progress = timelineProgressUnder(controllers[timelineDrag.controllerIndex])
        if (progress !== null) timelineDrag.lastProgress = progress
        const now = performance.now()
        if (progress !== null && now - lastScrubSeekAt >= SCRUB_SEEK_INTERVAL_MS) {
          lastScrubSeekAt = now
          ctx.onTimelineSeek?.(progress, 'preview')
        }
      }

      // AYNI (Fully VR): a grabbed sky turns, and the globe with it.
      for (const i of [0, 1] as const) {
        const drag = skyDrag[i]
        if (!drag) continue
        if (!ctx.canGrabSky?.()) {
          skyDrag[i] = null
          continue
        }
        if (!setRaycasterFromController(controllers[i])) continue
        skyDirScratch.copy(raycaster.ray.direction)
        skyDragGlobeQuaternion(THREE_, drag.globeStart, drag.startDir, skyDirScratch, ctx.globe.quaternion)
      }

      switch (rotationMode.kind) {
        case 'idle':
          break
        case 'single':
          updateSingle(rotationMode)
          break
        case 'two-hand':
          updateTwoHand(rotationMode)
          break
        case 'inertia':
          updateInertia(rotationMode, deltaSeconds)
          break
      }

      // Grab-and-carry — keep the grabbed offset pinned under the
      // controller: globe.position = controllerPos + grabOffset each
      // frame. Position only; the rotation modes above and pinch /
      // thumbstick zoom are orthogonal and keep working mid-carry.
      // Runs regardless of rotation mode so the user can carry with
      // one hand and rotate with the other. Place mode cancels an
      // active carry — the placement flow owns globe.position while
      // it's active (reticle / height rail write it per frame).
      if (carry.kind === 'carry') {
        if (ctx.placement?.isPlacing()) {
          carry = { kind: 'idle' }
        } else {
          const controller = controllers[carry.controllerIndex]
          controller.getWorldPosition(scratchVec3A)
          scratchVec3A.add(carry.grabOffset)
          ctx.globe.position.copy(scratchVec3A)
        }
      }

      // Browse panel touch drag-scroll. Pending → scroll transitions
      // when the pointer moves past the threshold; once scrolling,
      // feed the live UV to the panel each frame. Controller taps
      // (no movement) stay pending and resolve as a select on
      // release. Phones have no thumbstick, so this is their only
      // way to scroll the list.
      if (browseDrag.kind !== 'idle') {
        const controller = controllers[browseDrag.controllerIndex]
        setRaycasterFromController(controller)
        const hits = raycaster.intersectObject(ctx.browse.mesh, false)
        const uv = hits[0]?.uv
        if (uv) {
          if (browseDrag.kind === 'pending') {
            if (Math.abs(uv.y - browseDrag.startUvY) >= BROWSE_DRAG_THRESHOLD) {
              // Promote to a scroll and cancel any armed card select
              // so dragging on a card scrolls rather than selecting.
              const idx = browseDrag.controllerIndex
              if (browseArmed[idx]?.kind === 'browse-select') browseArmed[idx] = null
              ctx.browse.beginDrag({ x: browseDrag.startUvX, y: browseDrag.startUvY })
              ctx.browse.dragTo({ x: uv.x, y: uv.y })
              browseDrag = { kind: 'scroll', controllerIndex: idx }
            }
          } else {
            ctx.browse.dragTo({ x: uv.x, y: uv.y })
          }
        }
      }

      // Velocity tracker only runs during user-driven rotation;
      // inertia drives itself from a captured velocity and would
      // otherwise feed back into its own decay calculation.
      const isUserRotating =
        rotationMode.kind === 'single' || rotationMode.kind === 'two-hand'
      updateVelocityTracking(deltaSeconds, isUserRotating)

      // Thumbstick zoom runs in idle + single modes. Suppressed
      // during two-hand (pinch owns scale) and inertia (avoid
      // accidental scale during a flick spin).
      if (rotationMode.kind !== 'two-hand' && rotationMode.kind !== 'inertia') {
        updateThumbstickZoom(deltaSeconds)
      }

      // Update the visible laser dot + ray length last so it
      // reflects the current globe position after rotation/scale.
      // The try/catch is defensive — the grabbed-globe guard in
      // updateSingle and the stable-array design of
      // ctx.getAllGlobes() should prevent raycast errors, but a
      // silent frame-break on an unexpected throw would be worse
      // than a frame with missing dots. Log once so persistent
      // failures are visible in the Quest console without spamming
      // the log at XR frame rate.
      updateTooltips()
      updatePinButtons()

      try {
        updateRayVisuals()
      } catch (err) {
        if (!rayVisualErrorLogged) {
          rayVisualErrorLogged = true
          logger.warn('[VR] updateRayVisuals error (further errors suppressed):', err)
        }
      }
    },

    dispose() {
      // Three.js doesn't provide a `removeEventListener` for the
      // bound-lambda pattern without stashing references; instead
      // we remove the controllers from the scene entirely, which
      // releases the pose tracking and GC'd listeners with them.
      for (let i = 0; i < controllers.length; i++) {
        const controller = controllers[i]
        controller.remove(rayLines[i])
        ctx.scene.remove(controller)
        const line = rayLines[i]
        ;(line.geometry as THREE.BufferGeometry).dispose()
        ;(line.material as THREE.Material).dispose()
      }
      for (let i = 0; i < grips.length; i++) {
        // Removing the grip from the scene also removes the
        // controller-model child + releases its glTF resources via
        // Three.js' standard scene-graph disposal.
        ctx.scene.remove(grips[i])
      }
      for (const sprite of tooltips) {
        const mat = sprite.material as THREE.SpriteMaterial
        mat.map?.dispose()
        mat.dispose()
      }
      for (let i = 0; i < dots.length; i++) {
        const dot = dots[i]
        ctx.scene.remove(dot)
        ;(dot.geometry as THREE.BufferGeometry).dispose()
        ;(dot.material as THREE.Material).dispose()
      }
    },
  }
}
